// Authentication Service
import apiService from './api';

class AuthService {
  constructor() {
    this.api = apiService;

    // sessionStorage is per-tab; localStorage is shared across tabs.
    // Migrate/sync auth data so links opened in a new tab stay logged-in.
    this.syncStorage();
  }

  syncStorage() {
    try {
      const keys = ['session_token', 'user', 'tenant', 'userFeatures'];

      // If localStorage has values but sessionStorage doesn't (new tab), hydrate sessionStorage.
      keys.forEach((key) => {
        const sessionVal = sessionStorage.getItem(key);
        const localVal = localStorage.getItem(key);
        if (!sessionVal && localVal) {
          sessionStorage.setItem(key, localVal);
        }
      });

      // If sessionStorage has values but localStorage doesn't (existing logged-in tab),
      // persist to localStorage so other tabs can access.
      keys.forEach((key) => {
        const sessionVal = sessionStorage.getItem(key);
        const localVal = localStorage.getItem(key);
        if (sessionVal && !localVal) {
          localStorage.setItem(key, sessionVal);
        }
      });
    } catch (e) {
      // Ignore storage errors (private mode / blocked storage)
      console.warn('AuthService storage sync failed:', e);
    }
  }

  async login(credentials) {
    const response = await this.api.post('/auth/login', credentials);
    console.log('Login response:', response);
    console.log('Login response.data:', response.data);
    console.log('Login response.data.session_token:', response.data?.session_token);
    
    if (response.success) {
      // Store session data
      console.log('Login successful, storing session data', response);
      
      const sessionToken = response.data?.session_token;
      if (sessionToken) {
        sessionStorage.setItem('session_token', sessionToken);
        localStorage.setItem('session_token', sessionToken);
        console.log('✅ Stored session_token in sessionStorage:', sessionToken.substring(0, 20) + '...');
      } else {
        console.error('❌ No session_token in response.data:', response.data);
      }
      
      if (response.data?.user) {
        sessionStorage.setItem('user', JSON.stringify(response.data.user));
        localStorage.setItem('user', JSON.stringify(response.data.user));
        console.log('✅ Stored user in sessionStorage');
      }
      
      if (response.data?.tenant) {
        sessionStorage.setItem('tenant', JSON.stringify(response.data.tenant));
        localStorage.setItem('tenant', JSON.stringify(response.data.tenant));
        console.log('✅ Stored tenant in sessionStorage');
      }
      
      // Store features data if available
      if (response.data?.features) {
        sessionStorage.setItem('userFeatures', JSON.stringify(response.data.features));
        localStorage.setItem('userFeatures', JSON.stringify(response.data.features));
        console.log('✅ Stored user features:', response.data.features);
      }
      
      // Verify token was stored
      const storedToken = sessionStorage.getItem('session_token');
      console.log('🔍 Verification - Token in sessionStorage:', storedToken ? storedToken.substring(0, 20) + '...' : 'NOT FOUND');
    } else {
      console.error('❌ Login failed:', response);
    }
    
    return response;
  }

  async register(data) {
    return this.api.post('/auth/register', data);
  }

  async resendConfirmation(email) {
    return this.api.post('/auth/resend-confirmation', { email });
  }

  async checkMobileExists(mobile) {
    try {
      const response = await this.api.get(`/auth/check-mobile/${mobile}`);
      return response;
    } catch (error) {
      console.error('Error checking mobile:', error);
      throw error;
    }
  }

  async logout() {
    try {
      await this.api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear session data regardless of API call result
      sessionStorage.clear();
      localStorage.removeItem('session_token');
      localStorage.removeItem('user');
      localStorage.removeItem('tenant');
      localStorage.removeItem('userFeatures');
    }
  }

  async getCurrentUser() {
    const response = await this.api.get('/auth/me');
    return response;
  }

  async changePassword(currentPassword, newPassword, confirmPassword = newPassword) {
    await this.api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
      confirm_password: confirmPassword,
    });
  }

  isAuthenticated() {
    const token = sessionStorage.getItem('session_token') || localStorage.getItem('session_token');
    const user = sessionStorage.getItem('user') || localStorage.getItem('user');
    
    return !!token && !!user;
  }

  getUser() {
    const userStr = sessionStorage.getItem('user') || localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    
    // If user doesn't have tenant_id, try to get it from tenant object
    if (user && !user.tenant_id) {
      const tenant = this.getTenant();
      if (tenant && tenant.id) {
        user.tenant_id = tenant.id;
      }
    }
    
    return user;
  }

  getTenant() {
    const tenantStr = sessionStorage.getItem('tenant') || localStorage.getItem('tenant');
    return tenantStr ? JSON.parse(tenantStr) : null;
  }

  getSessionToken() {
    return sessionStorage.getItem('session_token') || localStorage.getItem('session_token');
  }

  getUserFeatures() {
    const featuresStr = sessionStorage.getItem('userFeatures') || localStorage.getItem('userFeatures');
    return featuresStr ? JSON.parse(featuresStr) : null;
  }

  // Get complete user data including features
  getCompleteUser() {
    const user = this.getUser();
    const features = this.getUserFeatures();
    
    if (user && features) {
      return {
        ...user,
        features: features
      };
    }
    
    return user;
  }

  // Refresh user features from the server
  async refreshUserFeatures() {
    try {
      const response = await this.getCurrentUser();
      if (response.success && response.data.features) {
        sessionStorage.setItem('userFeatures', JSON.stringify(response.data.features));
        localStorage.setItem('userFeatures', JSON.stringify(response.data.features));
        return response.data.features;
      }
      return null;
    } catch (error) {
      console.error('Error refreshing user features:', error);
      return null;
    }
  }

  // Clear cached user data and force refresh
  clearUserCache() {
    sessionStorage.removeItem('userFeatures');
    localStorage.removeItem('userFeatures');
    console.log('Cleared user features cache');
  }
}

const authService = new AuthService();
export default authService;
